import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  Modal,
  Row,
  Col,
  Button,
  Spin,
} from 'antd';
import { Document, Page, pdfjs } from 'react-pdf';
import { SpText } from 'components/DesignKit';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PdfContainer = styled.div`
  width: 100%;
  height: 50vh;
  border: solid 1px #d0d0d0;
  border-radius: 7px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
`;

const NavButton = styled(Button)`
  border-radius: 4px;
  background-color: ${({ disabled }) => (disabled ? '#c0c0c0' : '#12284a')} !important;
  color: #fff !important;
  & > span {
    color: #fff !important;
  }
`;

type Props = {
  pdfData: Object,
  pdfName: String,
  showPdfCard: Boolean,
  setShowPdfCard: Function,
  loading: Boolean,
};

const ViewInvoice = (props: Props) => {
  const {
    pdfData,
    pdfName,
    showPdfCard,
    setShowPdfCard,
    loading,
  } = props;

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // set pdf width dynamically on resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // set pdf width dynamically when the modal
  // is opened
  useEffect(() => {
    if (showPdfCard && containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [showPdfCard, containerRef.current]);

  const handlePrevious = () => {
    if (currentPage > 1) setCurrentPage((state) => state - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((state) => state + 1);
  };

  const onSuccessfulLoad = ({ numPages }) => {
    if (numPages >= 1) {
      setTotalPages(numPages);
    }
  };

  return (
    <Modal
      visible={showPdfCard}
      onCancel={() => setShowPdfCard(false)}
      footer={null}
      width="50%"
      bodyStyle={{ padding: 50 }}
      title={`File Name: ${pdfName}`}
    >
      <Spin
        spinning={loading}
        tip="Please wait..."
        size="large"
        delay={300}
      >
        <div style={{ padding: 16 }}>
          <PdfContainer ref={containerRef}>
            <Document file={{ data: pdfData }} onLoadSuccess={onSuccessfulLoad}>
              <Page pageNumber={currentPage} width={containerWidth - 32} />
            </Document>
          </PdfContainer>

          <Row
            gutter={16}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              marginTop: 12,
            }}
          >
            <Col span={4}>
              <NavButton
                type="primary"
                onClick={handlePrevious}
                disabled={currentPage <= 1}
              >
                <SpText>Previous</SpText>
              </NavButton>
            </Col>
            <Col span={16} style={{ textAlign: 'center' }}>
              <SpText>
                Page {currentPage} out of {totalPages}
              </SpText>
            </Col>
            <Col
              span={4}
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <NavButton
                type="primary"
                onClick={handleNext}
                disabled={currentPage >= totalPages}
              >
                <SpText>Next</SpText>
              </NavButton>
            </Col>
          </Row>
        </div>
      </Spin>
    </Modal>
  );
};

export default ViewInvoice;
